import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Apple, Boxes, Link2, Plus, School } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { AppleCareBadge, AsmBadge, CheckInText, MdmBadge } from '../components/device-badges';
import { AssetStatusBadge, DueBadge, PersonTypeBadge } from '../components/badges';
import { AssetSearch } from '../components/pickers';
import { useToast } from '../components/toast';
import { Badge, Button, Card, CardHeader, DescriptionList, EmptyState, ErrorState, Field, PageHeader, PageLoader, Select } from '../components/ui';
import { CampusBadge } from '../components/work-order-badges';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatDateTime, formatRelative, personName } from '../lib/format';
import { invalidateInventory, useCategories } from '../lib/hooks';
import { DEVICE_FAMILY_LABEL, formatStorage, PROVIDER_LABEL } from '../lib/integrations';
import type { ManagedDeviceDetail } from '../lib/types';

const yesNo = (value: boolean | null) => (value === null ? null : value ? 'Yes' : 'No');
const mono = (value: string | null) => value && <span className="font-mono text-xs">{value}</span>;

const PURCHASE_SOURCE_LABEL: Record<string, string> = { APPLE: 'Apple', RESELLER: 'Reseller', MANUALLY_ADDED: 'Added manually' };

export function ManagedDeviceDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const [categoryId, setCategoryId] = useState('');
  const [linkingExisting, setLinkingExisting] = useState(false);

  const query = useQuery({ queryKey: ['managed-device', id], queryFn: () => api.get<ManagedDeviceDetail>(`/managed-devices/${id}`) });

  const refresh = () => {
    invalidateInventory(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['managed-devices'] });
    void queryClient.invalidateQueries({ queryKey: ['managed-device'] });
  };
  const createAsset = useMutation({
    mutationFn: () => api.post<{ assetTag: string }>(`/managed-devices/${id}/create-asset`, { categoryId: categoryId || undefined }),
    onSuccess: (asset) => {
      refresh();
      toast.success(`Created asset ${asset.assetTag}`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const linkAsset = useMutation({
    mutationFn: (assetId: string) => api.put(`/assets/${assetId}/apple-device`, { managedDeviceId: id }),
    onSuccess: () => {
      refresh();
      setLinkingExisting(false);
      toast.success('Linked to asset');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (query.isLoading) return <PageLoader />;
  if (query.error || !query.data) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const device = query.data;
  const canWrite = can('TECHNICIAN');
  const inMdm = !!device.mdmProvider;
  const inAsm = !!device.asmSeenAt;
  const active = device.asset?.activeCheckout;
  const campus = device.network?.location && device.network.location !== 'UNKNOWN' ? device.network.location : null;
  // Jamf School reports the public address a device last checked in from.
  const publicIp = device.network?.publicIp ? (
    <span className="inline-flex flex-wrap items-center gap-2">
      {mono(device.network.publicIp)}
      {device.network.configured ? (
        <CampusBadge location={device.network.location} />
      ) : (
        canWrite && (
          <Link to="/settings" className="text-xs font-medium text-brand-700 hover:underline">
            Add your school’s public IP addresses to see on or off campus
          </Link>
        )
      )}
    </span>
  ) : null;
  const storage =
    device.storageCapacityMb !== null
      ? `${formatStorage(device.storageAvailableMb) ?? '?'} free of ${formatStorage(device.storageCapacityMb)}`
      : null;

  return (
    <>
      <PageHeader
        back={{ to: '/managed-devices', label: 'Managed devices' }}
        title={<span className="font-mono">{device.serialNumber}</span>}
        description={[device.name, device.model ?? device.asmDeviceModel].filter(Boolean).join(' · ') || DEVICE_FAMILY_LABEL[device.productFamily]}
        meta={
          <>
            <Badge>{DEVICE_FAMILY_LABEL[device.productFamily]}</Badge>
            <MdmBadge device={device} />
            {inAsm && <AsmBadge device={device} />}
            {campus && <CampusBadge location={campus} />}
            {inMdm && !device.mdmRemovedAt && (
              <Badge tone="gray">
                Checked in <CheckInText date={device.lastCheckInAt} />
              </Badge>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader
              title="Device management"
              description={inMdm ? `Inventory from ${PROVIDER_LABEL[device.mdmProvider!]}` : undefined}
            />
            <div className="p-5">
              {inMdm ? (
                <DescriptionList
                  columns={2}
                  items={[
                    { label: 'Last check-in', value: device.lastCheckInAt && `${formatRelative(device.lastCheckInAt)} · ${formatDateTime(device.lastCheckInAt)}` },
                    { label: 'Last inventory update', value: device.lastInventoryAt && formatDateTime(device.lastInventoryAt) },
                    { label: 'Operating system', value: [device.osName, device.osVersion, device.osBuild && `(${device.osBuild})`].filter(Boolean).join(' ') || null },
                    { label: 'Manufacturer', value: device.manufacturer },
                    { label: 'Compliance', value: device.complianceState },
                    { label: 'Encrypted', value: yesNo(device.encrypted) },
                    { label: 'Managed', value: yesNo(device.managed) },
                    { label: 'Supervised', value: yesNo(device.supervised) },
                    { label: 'Enrolled', value: device.enrolledAt && formatDate(device.enrolledAt) },
                    { label: 'Site', value: device.mdmSite },
                    { label: 'Building and room', value: [device.mdmBuilding, device.mdmRoom].filter(Boolean).join(' · ') || null },
                    { label: 'Battery', value: device.batteryLevel !== null ? `${device.batteryLevel}%` : null },
                    { label: 'Storage', value: storage },
                    device.network ? { label: 'Public IP address', value: publicIp } : { label: 'IP address', value: mono(device.ipAddress) },
                    { label: 'Wi-Fi MAC address', value: mono(device.wifiMacAddress) },
                    { label: 'Asset tag in the MDM', value: device.mdmAssetTag },
                    { label: 'Model identifier', value: mono(device.modelIdentifier) },
                    { label: 'UDID', value: mono(device.udid) },
                    { label: 'Removed from the MDM', value: device.mdmRemovedAt && formatDate(device.mdmRemovedAt) },
                  ]}
                />
              ) : (
                <EmptyState
                  icon={<Apple />}
                  title="Not enrolled in an MDM"
                  description={
                    device.releasedFromOrgAt
                      ? 'This device was released from Apple School Manager.'
                      : device.asmStatus === 'ASSIGNED'
                        ? `Apple School Manager assigns it to ${device.asmMdmServerName ?? 'your MDM server'}, so it enrolls the next time it’s set up.`
                        : inAsm
                          ? 'Assign it to your MDM server in Apple School Manager so it enrolls when it’s set up.'
                          : 'Neither Jamf nor Apple School Manager reports this device any more.'
                  }
                />
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Apple School Manager" description={inAsm ? 'Purchase and assignment record' : undefined} />
            <div className="p-5">
              {inAsm ? (
                <DescriptionList
                  columns={2}
                  items={[
                    { label: 'Assignment', value: <AsmBadge device={device} /> },
                    { label: 'MDM server', value: device.asmMdmServerName },
                    { label: 'Model', value: device.asmDeviceModel },
                    { label: 'Capacity and color', value: [device.asmCapacity, device.asmColor].filter(Boolean).join(' · ') || null },
                    { label: 'Part number', value: mono(device.asmPartNumber) },
                    { label: 'Order number', value: mono(device.orderNumber) },
                    { label: 'Ordered', value: device.orderedAt && formatDate(device.orderedAt) },
                    {
                      label: 'Purchased from',
                      value: device.purchaseSource && [PURCHASE_SOURCE_LABEL[device.purchaseSource] ?? device.purchaseSource, device.purchaseSourceId].filter(Boolean).join(' · '),
                    },
                    { label: 'Added to Apple School Manager', value: device.addedToOrgAt && formatDate(device.addedToOrgAt) },
                    { label: 'Released', value: device.releasedFromOrgAt && formatDate(device.releasedFromOrgAt) },
                  ]}
                />
              ) : (
                <EmptyState
                  icon={<School />}
                  title="Not listed in Apple School Manager"
                  description="Devices bought from Apple or an Apple Authorized Reseller appear here once Apple School Manager is connected."
                />
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Assigned user" description={inMdm ? `As set in ${PROVIDER_LABEL[device.mdmProvider!]}` : undefined} />
            <div className="p-5 text-sm">
              {device.assignedPerson ? (
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/people/${device.assignedPerson.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {personName(device.assignedPerson)}
                    </Link>
                    <p className="truncate text-xs text-slate-500">{device.assignedUserEmail}</p>
                  </div>
                  <PersonTypeBadge type={device.assignedPerson.type} />
                </div>
              ) : device.assignedUserName || device.assignedUserEmail || device.assignedUsername ? (
                <div>
                  <p className="font-medium text-slate-900">{device.assignedUserName ?? device.assignedUsername}</p>
                  <p className="text-xs text-slate-500">{device.assignedUserEmail ?? device.assignedUsername}</p>
                  <p className="mt-2 text-xs text-slate-400">No person in Edvora has this email.</p>
                </div>
              ) : (
                <p className="text-slate-500">No user assigned.</p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Asset record" />
            <div className="p-5">
              {device.asset ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/assets/${device.asset.id}`} className="font-mono text-sm font-semibold text-brand-800 hover:underline">
                      {device.asset.assetTag}
                    </Link>
                    <AssetStatusBadge status={device.asset.status} />
                  </div>
                  <p className="text-sm text-slate-600">{device.asset.name}</p>
                  {active ? (
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <p className="text-slate-500">Checked out to</p>
                      <p className="font-medium text-slate-900">
                        {active.personId ? (
                          <Link to={`/people/${active.personId}`} className="hover:text-brand-700">
                            {active.assigneeLabel}
                          </Link>
                        ) : (
                          active.assigneeLabel
                        )}
                      </p>
                      <DueBadge dueAt={active.dueAt} />
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Not checked out.</p>
                  )}
                </div>
              ) : canWrite ? (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">This device isn’t linked to an asset yet.</p>
                  {linkingExisting ? (
                    <>
                      <AssetSearch inline placeholder="Find an asset to link" onSelect={(asset) => linkAsset.mutate(asset.id)} />
                      <Button size="sm" variant="ghost" onClick={() => setLinkingExisting(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Field label="Category">
                        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                          <option value="">Based on the device type</option>
                          {categories.data?.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="primary" icon={<Plus />} loading={createAsset.isPending} onClick={() => createAsset.mutate()}>
                          Create asset
                        </Button>
                        <Button icon={<Link2 />} onClick={() => setLinkingExisting(true)}>
                          Link existing
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <EmptyState icon={<Boxes />} title="Not linked to an asset" />
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Coverage" description="Limited warranty or AppleCare, from Apple School Manager" />
            <div className="p-5">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Covered until', value: <AppleCareBadge endsAt={device.appleCareEndsAt} description={device.appleCareDescription} /> },
                  { label: 'Plan', value: device.appleCareDescription },
                  { label: 'Status', value: device.appleCareStatus && device.appleCareStatus.charAt(0) + device.appleCareStatus.slice(1).toLowerCase() },
                  { label: 'Checked', value: device.appleCareCheckedAt ? formatRelative(device.appleCareCheckedAt) : inAsm ? 'Not yet' : null },
                ]}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
